import React, { useContext, useEffect, useState } from 'react';
import { Container, Table, Button, Row, Col, Input, Form, FormGroup, Label, Badge, Card, CardBody } from 'reactstrap';
import PaginationMaterial from '../../components/PaginationMaterial/PaginationMaterial';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import Header from '../../components/Headers/Header';
import AdminNavbar from '../../components/Navbars/AdminNavbar/AdminNavbar';
import { AuthContext } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { CreateVendaModal } from '../../components/Venda/CreateVendaModal';
import ModalConfirm from '../../components/ModalConfirm/ModalConfirm';
import api from '../../api';
import Select from 'react-select';

interface VendaDTO {
    id: number;
    produtoId: number;
    produtoName: string;
    clienteId: number;
    clienteName: string;
    vendedorId: number;
    vendedorName: string;
    formaPagamentoValues: {
        id: number;
        formaPagamentoName: string | null;
        value: number;
    }[];
}

interface VendaPagedResponse {
    result: VendaDTO[];
    pageCount: number;
}

const Venda = () => {
    const showToast = useToast();
    const { objUserTk, handleLogout } = useContext(AuthContext);

    const [loading, setLoading] = useState(true);
    const [vendas, setVendas] = useState<VendaDTO[]>([]);
    const [pageCount, setPageCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 9;

    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [vendaToDelete, setVendaToDelete] = useState<number | null>(null);

    // Filtros
    const [vendedorIdFilter, setVendedorIdFilter] = useState(0);
    const [formaPagamentoIdFilter, setFormaPagamentoIdFilter] = useState(0);

    // Listas para filtros
    const [vendedoresList, setVendedoresList] = useState<{ id: number; name: string }[]>([]);
    const [formasPagamentoList, setFormasPagamentoList] = useState<{ id: number; name: string }[]>([]);

    useEffect(() => {
        loadVendas(1);
        loadVendedores();
        loadFormasPagamento();
    }, []);

    const loadVendas = async (page: number, vendedorId = 0, formaPagamentoId = 0) => {
        setLoading(true);
        try {
            const response = await api.get<VendaPagedResponse>('/Venda/getAllVendaPaged', {
                params: {
                    EmpresaId: objUserTk.EmpresaId,
                    PageNumber: page,
                    PageSize: pageSize,
                    VendedorId: vendedorId || undefined,
                    FormaPagamentoId: formaPagamentoId || undefined,
                },
            });

            if (response.data) {
                setVendas(response.data.result || []);
                setPageCount(response.data.pageCount || 0);
                setCurrentPage(page);
            }
        } catch (error: any) {
            if (error.response?.status === 401) {
                handleLogout();
                showToast('error', 'Sessão expirada. Faça login novamente.');
            } else {
                console.error('Erro ao carregar vendas:', error);
                showToast('error', 'Erro ao carregar vendas.');
            }
        } finally {
            setLoading(false);
        }
    };

    const loadVendedores = async () => {
        try {
            const response = await api.get('/Terceiro/getTerceiroPaged', {
                params: {
                    pageNumber: 1,
                    pageSize: 1000,
                    nome: '',
                    empresaId: objUserTk.EmpresaId,
                    tipoTerceiro: 'Vendedor',
                },
            });

            if (response.data?.result) {
                setVendedoresList(response.data.result);
            }
        } catch (error) {
            console.error('Erro ao carregar vendedores:', error);
        }
    };

    const loadFormasPagamento = async () => {
        try {
            const response = await api.get(`/FormaPagamento/getFormaPagamentoByEmpresaId/${objUserTk.EmpresaId}`);
            if (response.data) {
                setFormasPagamentoList(response.data);
            }
        } catch (error) {
            console.error('Erro ao carregar formas de pagamento:', error);
        }
    };

    const handlePageChange = (_: any, value: number) => {
        loadVendas(value, vendedorIdFilter, formaPagamentoIdFilter);
    };

    const handleFilterSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        loadVendas(1, vendedorIdFilter, formaPagamentoIdFilter);
    };

    const handleClearFilters = () => {
        setVendedorIdFilter(0);
        setFormaPagamentoIdFilter(0);
        loadVendas(1, 0, 0);
    };

    const handleDeleteVenda = async () => {
        if (!vendaToDelete) return;

        try {
            await api.delete(`/Venda/${vendaToDelete}`);
            showToast('success', 'Venda excluída com sucesso!');
            setDeleteModalOpen(false);
            setVendaToDelete(null);
            loadVendas(currentPage, vendedorIdFilter, formaPagamentoIdFilter);
        } catch (error: any) {
            if (error.response?.status === 401) {
                handleLogout();
                showToast('error', 'Sessão expirada. Faça login novamente.');
            } else if (error.response?.status === 400) {
                showToast('error', error.response.data || 'Erro ao excluir a venda.');
            } else {
                showToast('error', 'Erro ao excluir a venda.');
            }
        }
    };

    const handleDownloadContrato = async (vendaId: number) => {
        try {
            const response = await api.get(`/Venda/contrato/${vendaId}`, {
                responseType: 'blob',
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `contrato_venda_${vendaId}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            showToast('success', 'Contrato baixado com sucesso!');
        } catch (error: any) {
            if (error.response?.status === 401) {
                handleLogout();
                showToast('error', 'Sessão expirada. Faça login novamente.');
            } else if (error.response?.status === 404) {
                showToast('error', 'Contrato não encontrado.');
            } else {
                showToast('error', 'Erro ao baixar contrato.');
            }
        }
    };

    const getTotalVenda = (venda: VendaDTO) => {
        return venda.formaPagamentoValues.reduce((sum, fp) => sum + fp.value, 0);
    };

    const formatCurrency = (value: number) => {
        return `R$ ${value.toFixed(2).replace('.', ',')}`;
    };

    return (
        <>
            <LoadingSpinner />
            <Header />
            <AdminNavbar brandText="Gerenciar Vendas" search={false} titleSearch="" />

            <Container className="mt-5">
                {/* Cabeçalho com Título e Botão */}
                <Row className="align-items-center mb-4">
                    <Col xs="12" md="6">
                        <div>
                            <h2 className="mb-1">
                                <i className="fa fa-shopping-cart mr-2 text-primary"></i>
                                Gerenciar Vendas
                            </h2>
                            <p className="text-muted mb-0">Cadastre e gerencie as vendas de produtos e serviços</p>
                        </div>
                    </Col>
                    <Col xs="12" md="6" className="text-right">
                        <Button
                            style={{ color: "#FFF", backgroundColor: "#585858" }}
                            className="btn-filter"
                            onClick={() => setCreateModalOpen(true)}
                            size="lg"
                        >
                            <i className="fa fa-plus mr-2"></i>
                            Nova Venda
                        </Button>
                    </Col>
                </Row>

                <hr className="my-4" />

                {/* Filtros */}
                <Card className="shadow-sm mb-4">
                    <CardBody>
                        <h5 className="mb-3">
                            <i className="fa fa-filter mr-2"></i>
                            Filtros de Busca
                        </h5>
                        <Form onSubmit={handleFilterSubmit}>
                        <Row>
                            <Col md={5}>
                                <FormGroup>
                                    <Label>Vendedor</Label>
                                    <Select
                                        options={[
                                            { value: 0, label: 'Todos' },
                                            ...vendedoresList.map(v => ({ value: v.id, label: v.name }))
                                        ]}
                                        value={
                                            vendedorIdFilter
                                                ? { value: vendedorIdFilter, label: vendedoresList.find(v => v.id === vendedorIdFilter)?.name || '' }
                                                : { value: 0, label: 'Todos' }
                                        }
                                        onChange={(option: any) => setVendedorIdFilter(option?.value || 0)}
                                        placeholder="Selecione..."
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={5}>
                                <FormGroup>
                                    <Label>Forma de Pagamento</Label>
                                    <Select
                                        options={[
                                            { value: 0, label: 'Todas' },
                                            ...formasPagamentoList.map(f => ({ value: f.id, label: f.name }))
                                        ]}
                                        value={
                                            formaPagamentoIdFilter
                                                ? { value: formaPagamentoIdFilter, label: formasPagamentoList.find(f => f.id === formaPagamentoIdFilter)?.name || '' }
                                                : { value: 0, label: 'Todas' }
                                        }
                                        onChange={(option: any) => setFormaPagamentoIdFilter(option?.value || 0)}
                                        placeholder="Selecione..."
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={2} className="d-flex align-items-end">
                                <FormGroup className="w-100">
                                    <Button
                                        style={{ color: "#FFF", backgroundColor: "#585858" }}
                                        type="submit"
                                        block
                                    >
                                        <i className="fa fa-search mr-2"></i>
                                        Filtrar
                                    </Button>
                                    <Button
                                        style={{ color: "#FFF", backgroundColor: "#d9534f" }}
                                        block
                                        onClick={handleClearFilters}
                                        className="mt-2"
                                    >
                                        Limpar
                                    </Button>
                                </FormGroup>
                            </Col>
                        </Row>
                        </Form>
                    </CardBody>
                </Card>

                {/* Tabela de Vendas */}
                <Row className="mt-4">
                    <Col>
                        <Table hover responsive className="table-striped custom-table">
                            <thead>
                                <tr>
                                    <th>Nº Venda</th>
                                    <th>Cliente</th>
                                    <th>Produto</th>
                                    <th>Vendedor</th>
                                    <th>Formas de Pagamento</th>
                                    <th>Total</th>
                                    <th>Ações</th>
                                </tr>
                            </thead>
                                <tbody>
                                    {vendas.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} className="text-center">
                                                <em>Nenhuma venda cadastrada.</em>
                                            </td>
                                        </tr>
                                    ) : (
                                        vendas.map((venda) => (
                                            <tr key={venda.id}>
                                                <td>
                                                    <Badge color="primary" pill>
                                                        #{venda.id}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    <div>
                                                        <strong>{venda.clienteName}</strong>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div>
                                                        <i className="fa fa-box mr-2 text-muted"></i>
                                                        {venda.produtoName}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div>
                                                        <i className="fa fa-user-tie mr-2 text-muted"></i>
                                                        {venda.vendedorName}
                                                    </div>
                                                </td>
                                                <td>
                                                    {venda.formaPagamentoValues.map((fp, index) => (
                                                        <div key={fp.id || index} className="mb-1">
                                                            <Badge color="info" className="mr-1">
                                                                {fp.formaPagamentoName || 'Não definida'}
                                                            </Badge>
                                                            <small>{formatCurrency(fp.value)}</small>
                                                        </div>
                                                    ))}
                                                </td>
                                                <td>
                                                    <h5 className="mb-0 text-success">
                                                        {formatCurrency(getTotalVenda(venda))}
                                                    </h5>
                                                </td>
                                                <td>
                                                    <Button
                                                        title="Baixar Contrato PDF"
                                                        onClick={() => handleDownloadContrato(venda.id)}
                                                    >
                                                        <i className="fa fa-file-pdf"></i>
                                                    </Button>{' '}
                                                    <Button
                                                        color="danger"
                                                        title="Excluir Venda"
                                                        onClick={() => {
                                                            setVendaToDelete(venda.id);
                                                            setDeleteModalOpen(true);
                                                        }}
                                                    >
                                                        <i className="fa fa-trash"></i>
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                        </Table>

                        {/* Paginação */}
                        {pageCount > 1 && (
                            <div className="d-flex justify-content-center mt-4">
                                <PaginationMaterial
                                    count={pageCount}
                                    onChange={handlePageChange}
                                />
                            </div>
                        )}
                    </Col>
                </Row>
            </Container>

            {/* Modal de Criação */}
            <CreateVendaModal
                isOpen={createModalOpen}
                toggle={() => setCreateModalOpen(false)}
                onSuccess={() => {
                    setCreateModalOpen(false);
                    loadVendas(currentPage, vendedorIdFilter, formaPagamentoIdFilter);
                }}
            />

            {/* Modal de Confirmação de Exclusão */}
            <ModalConfirm
                openModal={deleteModalOpen}
                closeModal={() => {
                    setDeleteModalOpen(false);
                    setVendaToDelete(null);
                }}
                handleConfirm={handleDeleteVenda}
            />
        </>
    );
};

export default Venda;
